import type { TripTemplate, TemplateDay, TravelStyle } from '@/types/tripTemplates';

/** Builder draft shape (matches TemplateBuilder internal draft). */
export type TemplateBuilderDraft = {
  title: string;
  destination: string;
  duration: number;
  shortDescription: string;
  longDescription?: string;
  coverImage?: string;
  coverImageCredit?: string;
  coverImageCreditUrl?: string;
  difficulty: TripTemplate['difficulty'];
  travelStyle: TravelStyle[];
  estimatedBudget: TripTemplate['estimatedBudget'];
  itinerary: TemplateDay[];
  bestMonths?: string[];
  tags?: string[];
  highlights?: string[];
  destinations?: TripTemplate['destinations'];
};

export function templateToBuilderDraft(t: TripTemplate): Partial<TemplateBuilderDraft> {
  return {
    title: t.title,
    destination: t.destination,
    duration: t.duration,
    shortDescription: t.shortDescription,
    longDescription: t.longDescription,
    coverImage: t.coverImage,
    coverImageCredit: t.coverImageCredit,
    coverImageCreditUrl: t.coverImageCreditUrl,
    difficulty: t.difficulty,
    travelStyle: [...(t.travelStyle ?? [])],
    estimatedBudget: t.estimatedBudget
      ? {
          ...t.estimatedBudget,
          breakdown: { ...t.estimatedBudget.breakdown },
        }
      : undefined,
    itinerary: JSON.parse(JSON.stringify(t.itinerary ?? [])) as TemplateDay[],
    bestMonths: [...(t.bestMonths ?? [])],
    tags: [...(t.tags ?? [])],
    highlights: [...(t.highlights ?? [])],
    destinations: t.destinations?.map((d) => ({
      ...d,
      startDate: d.startDate instanceof Date ? d.startDate : new Date(d.startDate),
      endDate: d.endDate instanceof Date ? d.endDate : new Date(d.endDate),
    })),
  };
}

/** Clone an existing template into a new builder draft (duplicate flow). */
export function cloneTemplateAsNewDraft(t: TripTemplate): Partial<TemplateBuilderDraft> {
  const base = templateToBuilderDraft(t);
  const titleBase = (t.title || 'Template').replace(/ \(Copy(?: \d+)?\)$/i, '').trim();
  return {
    ...base,
    title: `${titleBase} (Copy)`,
  };
}

export function getTemplateDisplayStatus(t: TripTemplate): 'draft' | 'live' | 'pending' | 'rejected' {
  if (t.status === 'draft') return 'draft';
  if (t.status === 'pending') return 'pending';
  if (t.status === 'rejected') return 'rejected';
  return 'live';
}
